import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import ledgerService from '../../services/ledgerService';

// --- Async Thunks ---

export const fetchLedgerDashboard = createAsyncThunk(
  'ledger/fetchDashboard',
  async (_, { rejectWithValue }) => {
    try {
      const res = await ledgerService.getDashboard();
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch ledger dashboard');
    }
  }
);

export const fetchLedgerEntries = createAsyncThunk(
  'ledger/fetchEntries',
  async (params, { rejectWithValue }) => {
    try {
      const res = await ledgerService.getEntries(params);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch ledger entries');
    }
  }
);

export const fetchLedgerJournal = createAsyncThunk(
  'ledger/fetchJournal',
  async (journalId, { rejectWithValue }) => {
    try {
      const res = await ledgerService.getJournal(journalId);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch journal');
    }
  }
);

export const fetchLedgerRevenue = createAsyncThunk(
  'ledger/fetchRevenue',
  async (params, { rejectWithValue }) => {
    try {
      const res = await ledgerService.getRevenue(params);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch ledger revenue');
    }
  }
);

export const verifyLedgerChain = createAsyncThunk(
  'ledger/verifyChain',
  async (fromSequence, { rejectWithValue }) => {
    try {
      const res = await ledgerService.verifyChain(fromSequence);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Chain verification failed');
    }
  }
);

export const fetchReconciliation = createAsyncThunk(
  'ledger/fetchReconciliation',
  async (date, { rejectWithValue }) => {
    try {
      const res = await ledgerService.getReconciliation(date);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch reconciliation report');
    }
  }
);

export const fetchReversals = createAsyncThunk(
  'ledger/fetchReversals',
  async (_, { rejectWithValue }) => {
    try {
      const res = await ledgerService.getReversals();
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch reversals');
    }
  }
);

export const createReversal = createAsyncThunk(
  'ledger/createReversal',
  async (data, { rejectWithValue }) => {
    try {
      const res = await ledgerService.createReversal(data);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to create reversal');
    }
  }
);

export const approveReversal = createAsyncThunk(
  'ledger/approveReversal',
  async (reversalId, { rejectWithValue }) => {
    try {
      const res = await ledgerService.approveReversal(reversalId);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to approve reversal');
    }
  }
);

// --- Initial State ---

const initialState = {
  dashboard: null,
  entries: { items: [], total: 0, limit: 25, offset: 0 },
  journal: null,
  revenue: null,
  chain: null,
  reconciliation: null,
  reversals: [],

  loading: {
    dashboard: false,
    entries: false,
    journal: false,
    revenue: false,
    chain: false,
    reconciliation: false,
    reversals: false,
    createReversal: false,
  },
  error: {
    dashboard: null,
    entries: null,
    journal: null,
    revenue: null,
    chain: null,
    reconciliation: null,
    reversals: null,
    createReversal: null,
  },
};

// --- Slice ---

const ledgerSlice = createSlice({
  name: 'ledger',
  initialState,
  reducers: {
    clearJournal(state) {
      state.journal = null;
      state.error.journal = null;
    },
    clearChain(state) {
      state.chain = null;
      state.error.chain = null;
    },
    clearError(state, action) {
      if (action.payload) {
        state.error[action.payload] = null;
      } else {
        Object.keys(state.error).forEach(k => { state.error[k] = null; });
      }
    },
  },
  extraReducers: (builder) => {
    // Dashboard
    builder
      .addCase(fetchLedgerDashboard.pending, (state) => { state.loading.dashboard = true; state.error.dashboard = null; })
      .addCase(fetchLedgerDashboard.fulfilled, (state, action) => { state.loading.dashboard = false; state.dashboard = action.payload; })
      .addCase(fetchLedgerDashboard.rejected, (state, action) => { state.loading.dashboard = false; state.error.dashboard = action.payload; });

    // Entries
    builder
      .addCase(fetchLedgerEntries.pending, (state) => { state.loading.entries = true; state.error.entries = null; })
      .addCase(fetchLedgerEntries.fulfilled, (state, action) => {
        state.loading.entries = false;
        state.entries = {
          items: action.payload.entries || [],
          total: action.payload.total || 0,
          limit: action.payload.limit || 25,
          offset: action.payload.offset || 0,
        };
      })
      .addCase(fetchLedgerEntries.rejected, (state, action) => { state.loading.entries = false; state.error.entries = action.payload; });

    // Journal
    builder
      .addCase(fetchLedgerJournal.pending, (state) => { state.loading.journal = true; state.error.journal = null; })
      .addCase(fetchLedgerJournal.fulfilled, (state, action) => { state.loading.journal = false; state.journal = action.payload; })
      .addCase(fetchLedgerJournal.rejected, (state, action) => { state.loading.journal = false; state.error.journal = action.payload; });

    // Revenue
    builder
      .addCase(fetchLedgerRevenue.pending, (state) => { state.loading.revenue = true; state.error.revenue = null; })
      .addCase(fetchLedgerRevenue.fulfilled, (state, action) => { state.loading.revenue = false; state.revenue = action.payload; })
      .addCase(fetchLedgerRevenue.rejected, (state, action) => { state.loading.revenue = false; state.error.revenue = action.payload; });

    // Chain verification
    builder
      .addCase(verifyLedgerChain.pending, (state) => { state.loading.chain = true; state.error.chain = null; state.chain = null; })
      .addCase(verifyLedgerChain.fulfilled, (state, action) => { state.loading.chain = false; state.chain = action.payload; })
      .addCase(verifyLedgerChain.rejected, (state, action) => { state.loading.chain = false; state.error.chain = action.payload; });

    // Reconciliation
    builder
      .addCase(fetchReconciliation.pending, (state) => { state.loading.reconciliation = true; state.error.reconciliation = null; })
      .addCase(fetchReconciliation.fulfilled, (state, action) => { state.loading.reconciliation = false; state.reconciliation = action.payload; })
      .addCase(fetchReconciliation.rejected, (state, action) => { state.loading.reconciliation = false; state.error.reconciliation = action.payload; });

    // Reversals
    builder
      .addCase(fetchReversals.pending, (state) => { state.loading.reversals = true; })
      .addCase(fetchReversals.fulfilled, (state, action) => { state.loading.reversals = false; state.reversals = action.payload || []; })
      .addCase(fetchReversals.rejected, (state, action) => { state.loading.reversals = false; state.error.reversals = action.payload; });

    builder
      .addCase(createReversal.pending, (state) => { state.loading.createReversal = true; state.error.createReversal = null; })
      .addCase(createReversal.fulfilled, (state, action) => {
        state.loading.createReversal = false;
        if (action.payload) state.reversals = [action.payload, ...state.reversals];
      })
      .addCase(createReversal.rejected, (state, action) => { state.loading.createReversal = false; state.error.createReversal = action.payload; });

    builder
      .addCase(approveReversal.fulfilled, () => {
        // Refresh will happen via fetchReversals
      });
  },
});

export const { clearJournal, clearChain, clearError } = ledgerSlice.actions;
export default ledgerSlice.reducer;
